/**
 * Shared bezier math for the curved-edge layout.
 *
 * Edges and useForceLayout both need to know the same arc shape:
 * Edges samples it at many t values to tessellate the curve; useForceLayout
 * samples it at one t value per interception node to place that node on the
 * arc midpoint. Centralizing the math keeps the geometry in lockstep across
 * components.
 */

/**
 * Quadratic Bezier control point — the chord midpoint pushed outward from
 * origin proportionally to chord length and midpoint-to-origin distance.
 * Edges and useForceLayout must use the identical formula or interception
 * nodes will appear to float off their arcs.
 */
export function bezierControl(
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
): { cx: number; cy: number; cz: number } {
  const mx = (ax + bx) * 0.5;
  const my = (ay + by) * 0.5;
  const mz = (az + bz) * 0.5;
  const midR = Math.hypot(mx, my, mz);

  let dx: number, dy: number, dz: number;
  if (midR > 0.4) {
    dx = mx / midR; dy = my / midR; dz = mz / midR;
  } else {
    const ar = Math.hypot(ax, ay, az) || 1;
    const br = Math.hypot(bx, by, bz) || 1;
    dx = ax / ar + bx / br;
    dy = ay / ar + by / br;
    dz = az / ar + bz / br;
    const dl = Math.hypot(dx, dy, dz);
    if (dl > 0.0001) { dx /= dl; dy /= dl; dz /= dl; }
    else { dx = 0; dy = 1; dz = 0; }
  }

  const chord = Math.hypot(bx - ax, by - ay, bz - az);
  const push = chord * 0.22 + midR * 0.18;

  return {
    cx: mx + dx * push,
    cy: my + dy * push,
    cz: mz + dz * push,
  };
}

/**
 * Sample a quadratic Bezier with control point `c` at parametric position `t`.
 * Writes result into out[outOffset .. outOffset+2].
 */
export function bezierSampleInto(
  ax: number, ay: number, az: number,
  cx: number, cy: number, cz: number,
  bx: number, by: number, bz: number,
  t: number,
  out: Float32Array,
  outOffset: number,
): void {
  const it = 1 - t;
  out[outOffset + 0] = it * it * ax + 2 * it * t * cx + t * t * bx;
  out[outOffset + 1] = it * it * ay + 2 * it * t * cy + t * t * by;
  out[outOffset + 2] = it * it * az + 2 * it * t * cz + t * t * bz;
}

// ---------------------------------------------------------------------
// Sub-arc geometry — the single source of truth for WHERE an arc bows.
//
// Both Edges.tsx (which tessellates the visible curve) and EdgeFlow.tsx
// (which rides cones along it) must agree exactly, or the cones drift off
// their lines. Everything below is pure so both can call it per frame.
// ---------------------------------------------------------------------

/** Lateral fan offset between adjacent sub-arcs of an expanded sleeve. */
export const SUB_ARC_LINEAR_OFFSET = 2.4;
/** Ring radius for cylindrical fans (sleeves with >= CYLINDER_THRESHOLD). */
export const SUB_ARC_RING_RADIUS = 2.2;
/** Sleeve size at which the fan switches from flat to cylindrical. */
export const CYLINDER_THRESHOLD = 6;
/**
 * How far apart the two directions of a bidirectional pair bow. Each side
 * is pushed ±this along the transverse axis, separating the drawn curves
 * by 2× this at their midpoints.
 * bezierControl() is symmetric in its endpoints, so A→B and B→A would
 * otherwise land on the IDENTICAL curve — drawn on top of each other,
 * with only the topmost clickable. Pushing each one the opposite way
 * along the transverse axis gives a two-lane road.
 */
export const BIDIRECTIONAL_SEPARATION = 1.6;

export type ArcAxes = {
  ax: number; ay: number; az: number;
  bx: number; by: number; bz: number;
};

/**
 * Two unit axes perpendicular to the chord, used to fan sub-arcs and to
 * separate bidirectional pairs.
 *
 * axisA = chord × outward (outward = radial direction at the chord
 * midpoint), which lies in the "transverse" plane. When chord and
 * outward are colinear — every spoke that passes through the origin,
 * e.g. all the edges into Game Over — the cross product collapses, so we
 * fall back to chord × world-up, then chord × world-right.
 */
export function arcAxes(
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
): ArcAxes {
  const chordX = bx - ax, chordY = by - ay, chordZ = bz - az;
  const chordLen = Math.hypot(chordX, chordY, chordZ) || 1;
  const cnx = chordX / chordLen, cny = chordY / chordLen, cnz = chordZ / chordLen;

  const mx = (ax + bx) * 0.5, my = (ay + by) * 0.5, mz = (az + bz) * 0.5;
  const midR = Math.hypot(mx, my, mz) || 1;
  const onx = mx / midR, ony = my / midR, onz = mz / midR;

  let aX = cny * onz - cnz * ony;
  let aY = cnz * onx - cnx * onz;
  let aZ = cnx * ony - cny * onx;
  let aLen = Math.hypot(aX, aY, aZ);
  if (aLen < 1e-4) {
    aX = -cnz; aY = 0; aZ = cnx;
    aLen = Math.hypot(aX, aY, aZ);
    if (aLen < 1e-4) {
      aX = 0; aY = cnz; aZ = -cny;
      aLen = Math.hypot(aX, aY, aZ) || 1;
    }
  }
  aX /= aLen; aY /= aLen; aZ /= aLen;

  // axisB completes the frame: chord × axisA.
  const bX = cny * aZ - cnz * aY;
  const bY = cnz * aX - cnx * aZ;
  const bZ = cnx * aY - cny * aX;
  const bLen = Math.hypot(bX, bY, bZ) || 1;

  return {
    ax: aX, ay: aY, az: aZ,
    bx: bX / bLen, by: bY / bLen, bz: bZ / bLen,
  };
}

/**
 * Control point for one sub-arc of a sleeve.
 *
 * @param arcIdx     index within the sleeve
 * @param moveCount  how many moves the sleeve carries
 * @param expanded   sleeve is fanned (hovered / toggle set to expanded)
 * @param dirSign    ±1 to separate a bidirectional pair, 0 when the edge
 *                   has no reverse counterpart
 */
export function subArcControl(
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
  arcIdx: number,
  moveCount: number,
  expanded: boolean,
  dirSign: number,
): { cx: number; cy: number; cz: number } {
  const nat = bezierControl(ax, ay, az, bx, by, bz);
  let cx = nat.cx, cy = nat.cy, cz = nat.cz;
  if (!expanded && dirSign === 0) return { cx, cy, cz };

  // CANONICAL axis order — this matters. arcAxes computes
  // `chord × outward`, so calling it with the endpoints swapped NEGATES
  // the axis. A→B and B→A also carry opposite dirSigns, so the two sign
  // flips cancelled exactly and both directions were pushed to the same
  // side — the bug that made bidirectional pairs render as one line no
  // matter how large the separation got. Deriving the axis from a fixed
  // endpoint ordering makes it direction-independent, so dirSign alone
  // decides the side.
  const flip =
    ax > bx || (ax === bx && (ay > by || (ay === by && az > bz)));
  const axes = flip
    ? arcAxes(bx, by, bz, ax, ay, az)
    : arcAxes(ax, ay, az, bx, by, bz);

  // Bidirectional separation applies whether or not the sleeve is fanned
  // — the two directions must never share a curve.
  if (dirSign !== 0) {
    const d = dirSign * BIDIRECTIONAL_SEPARATION;
    cx += axes.ax * d; cy += axes.ay * d; cz += axes.az * d;
  }

  if (expanded) {
    if (moveCount < CYLINDER_THRESHOLD) {
      const offset = (arcIdx - (moveCount - 1) / 2) * SUB_ARC_LINEAR_OFFSET;
      cx += axes.ax * offset; cy += axes.ay * offset; cz += axes.az * offset;
    } else {
      const angle = (2 * Math.PI * arcIdx) / moveCount;
      const ca = Math.cos(angle), sa = Math.sin(angle);
      cx += (ca * axes.ax + sa * axes.bx) * SUB_ARC_RING_RADIUS;
      cy += (ca * axes.ay + sa * axes.by) * SUB_ARC_RING_RADIUS;
      cz += (ca * axes.az + sa * axes.bz) * SUB_ARC_RING_RADIUS;
    }
  }
  return { cx, cy, cz };
}
